// Admin portal pieces: the backend access check and the site content editor
import React, { useEffect, useState } from 'react';
import { adminApi } from '../utils/adminApi';
import { DEFAULT_SITE_CONTENT } from '../data/siteContent';
import { mergeSiteContent, useSiteContent } from '../contexts/SiteContentContext';
import { PLAYBOOK_TYPES } from '../types/picks';

const titleCase = (type) => type.charAt(0).toUpperCase() + type.slice(1);
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// ---- Backend access ------------------------------------------------------

// Shows whether the backend's Cloud Run service account can do what the portal needs
export function BackendAccess() {
  const [state, setState] = useState({ loading: true });

  const check = async () => {
    setState({ loading: true });
    try {
      setState({ loading: false, result: await adminApi.health() });
    } catch (e) {
      setState({ loading: false, error: e.message });
    }
  };

  useEffect(() => {
    check();
  }, []);

  return (
    <section className="admin-panel" aria-labelledby="backend-access-title">
      <div className="admin-panel-head">
        <h2 id="backend-access-title">Backend access</h2>
        <button type="button" className="btn btn-sm" onClick={check} disabled={state.loading}>Check again</button>
      </div>
      {state.loading && <p className="admin-note">Checking…</p>}
      {state.error && <p className="admin-error">Could not reach the admin API: {state.error}</p>}
      {state.result && (
        <>
          <ul className="admin-checks">
            {state.result.checks.map((c) => (
              <li key={c.name} className={c.ok ? 'ok' : 'fail'}>
                <span className="admin-check-mark" aria-hidden="true">{c.ok ? '✓' : '✕'}</span>
                <span className="admin-check-name">{c.name}</span>
                {!c.ok && <span className="admin-secondary">Needs {c.need}</span>}
              </li>
            ))}
          </ul>
          {!state.result.ok && (
            <p className="admin-note">
              Grant the missing role to the odikai-backend Cloud Run service account in Google Cloud Console
              (IAM &amp; Admin → IAM), then check again.
            </p>
          )}
        </>
      )}
    </section>
  );
}

// ---- Site content --------------------------------------------------------

// Paragraphs are edited as one text box, separated by blank lines
const toText = (paragraphs) => (paragraphs || []).join('\n\n');
const toParagraphs = (text) => text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

// A collapsible section, closed until the admin opens it
function SectionShell({ id, title, note, isDefault, dirty, saving, onSave, onReset, children }) {
  const [open, setOpen] = useState(false);
  const bodyId = `section-${id}`;
  return (
    <section className={`admin-panel admin-collapsible${open ? ' is-open' : ''}`}>
      <div className="admin-panel-head">
        <div>
          <h2>
            <button type="button" className="admin-collapse-toggle" aria-expanded={open} aria-controls={bodyId}
              onClick={() => setOpen((o) => !o)}>
              <span className="admin-collapse-chevron" aria-hidden="true">›</span>
              {title}
            </button>
          </h2>
          {note && <p className="admin-secondary">{note}</p>}
        </div>
        <span className="admin-collapse-tags">
          {dirty && <span className="tag admin-tag-unsaved">Unsaved</span>}
          <span className={`tag${isDefault ? '' : ' tag-purple'}`}>{isDefault ? 'Default' : 'Edited'}</span>
        </span>
      </div>
      <div id={bodyId} hidden={!open}>
      {children}
      <div className="admin-panel-actions">
        <button type="button" className="btn btn-primary btn-sm" onClick={onSave} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {!isDefault && (
          <button type="button" className="btn btn-sm" onClick={onReset} disabled={saving}>Reset to default</button>
        )}
      </div>
      </div>
    </section>
  );
}

function FaqEditor({ items, onChange }) {
  const update = (i, patch) => onChange(items.map((item, j) => (j === i ? { ...item, ...patch } : item)));
  const move = (i, by) => {
    const next = [...items];
    const [item] = next.splice(i, 1);
    next.splice(i + by, 0, item);
    onChange(next);
  };
  return (
    <div className="admin-faq">
      {items.map((item, i) => (
        <div className="admin-faq-item" key={i}>
          <div className="admin-faq-fields">
            <label className="field-label" htmlFor={`q-${i}`}>Question {i + 1}</label>
            <input id={`q-${i}`} className="input" value={item.question} maxLength={300}
              onChange={(e) => update(i, { question: e.target.value })} />
            <label className="field-label" htmlFor={`a-${i}`}>Answer (blank line between paragraphs)</label>
            <textarea id={`a-${i}`} className="input admin-textarea" rows={4} value={item.text}
              onChange={(e) => update(i, { text: e.target.value })} />
          </div>
          <div className="admin-faq-tools">
            <button type="button" className="btn btn-sm" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move question ${i + 1} up`}>↑</button>
            <button type="button" className="btn btn-sm" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label={`Move question ${i + 1} down`}>↓</button>
            <button type="button" className="btn btn-sm btn-danger" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label={`Remove question ${i + 1}`}>Remove</button>
          </div>
        </div>
      ))}
      <button type="button" className="btn btn-sm" onClick={() => onChange([...items, { question: '', text: '' }])} disabled={items.length >= 40}>
        Add question
      </button>
    </div>
  );
}

// The form state for each section, from the merged (saved or default) content
function formsFrom(content) {
  return {
    banner: { ...content.banner },
    hero: { ...content.hero },
    home_faq: content.home_faq.map((f) => ({ question: f.question, text: toText(f.answer) })),
    faq: content.faq.map((f) => ({ question: f.question, text: toText(f.answer) })),
    playbook_descriptions: { ...content.playbook_descriptions },
  };
}

// What gets saved for a section, or an error message
function payloadFor(key, form) {
  if (key === 'banner') {
    const banner = {
      enabled: form.enabled === true,
      text: form.text.trim(),
      link_label: (form.link_label || '').trim(),
      link_url: (form.link_url || '').trim(),
    };
    if (banner.enabled && !banner.text) return { error: 'Write the announcement before turning it on.' };
    if (Boolean(banner.link_label) !== Boolean(banner.link_url)) return { error: 'A link needs both its text and where it goes.' };
    return { value: banner };
  }
  if (key === 'hero') {
    if (!form.title.trim()) return { error: 'The headline cannot be empty.' };
    return { value: { title: form.title.trim(), subtitle: form.subtitle.trim() } };
  }
  if (key === 'home_faq' || key === 'faq') {
    const list = form.map((f) => ({ question: f.question.trim(), answer: toParagraphs(f.text) }));
    if (list.some((f) => !f.question || f.answer.length === 0)) return { error: 'Every question needs a question and an answer.' };
    return { value: list };
  }
  const descriptions = Object.fromEntries(Object.entries(form).map(([t, text]) => [t, (text || '').trim()]));
  return { value: descriptions };
}

export function SiteContentEditor({ notify }) {
  const { reload: reloadSite } = useSiteContent();
  const [stored, setStored] = useState(null);
  const [forms, setForms] = useState(null);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      const raw = await adminApi.getContent();
      setStored(raw);
      setForms(formsFrom(mergeSiteContent(raw)));
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (error) return <div className="empty-state"><p className="admin-error">{error}</p><button type="button" className="btn" onClick={load}>Try again</button></div>;
  if (!forms) return <div className="loading"><div className="loading-spinner"></div><span>Loading content…</span></div>;

  const baseline = formsFrom(mergeSiteContent(stored));
  const setForm = (key, value) => setForms((f) => ({ ...f, [key]: value }));

  const save = async (key, reset = false) => {
    let value = null;
    if (!reset) {
      const result = payloadFor(key, forms[key]);
      if (result.error) { notify(result.error, true); return; }
      value = result.value;
      // Saving exactly the default text keeps the section on the default
      if (same(value, DEFAULT_SITE_CONTENT[key])) value = null;
    }
    setSaving(key);
    try {
      await adminApi.updateContent({ [key]: value });
      notify(reset ? 'Back to the default. Live on the site now.' : 'Saved. Live on the site now.');
      await load();
      reloadSite();
    } catch (e) {
      notify(e.message, true);
    } finally {
      setSaving(null);
    }
  };

  const shell = (key, title, note) => ({
    id: key,
    title,
    note,
    isDefault: !stored || stored[key] === undefined,
    dirty: !same(forms[key], baseline[key]),
    saving: saving === key,
    onSave: () => save(key),
    onReset: () => save(key, true),
  });

  const banner = forms.banner;
  return (
    <div className="admin-content">
      <SectionShell {...shell('banner', 'Announcement banner', 'A strip under the menu on every page. Visitors can dismiss it.')}>
        <label className="admin-check-row">
          <input type="checkbox" checked={banner.enabled === true}
            onChange={(e) => setForm('banner', { ...banner, enabled: e.target.checked })} />
          Show the banner
        </label>
        <label className="field-label" htmlFor="banner-text">Message</label>
        <input id="banner-text" className="input" maxLength={300} value={banner.text}
          placeholder="Picks for UFC 334 are up."
          onChange={(e) => setForm('banner', { ...banner, text: e.target.value })} />
        <div className="admin-grid-2">
          <div>
            <label className="field-label" htmlFor="banner-link-label">Link text (optional)</label>
            <input id="banner-link-label" className="input" maxLength={60} value={banner.link_label || ''}
              placeholder="See picks"
              onChange={(e) => setForm('banner', { ...banner, link_label: e.target.value })} />
          </div>
          <div>
            <label className="field-label" htmlFor="banner-link-url">Link to (optional)</label>
            <input id="banner-link-url" className="input" maxLength={500} value={banner.link_url || ''}
              placeholder="/picks or https://…"
              onChange={(e) => setForm('banner', { ...banner, link_url: e.target.value })} />
          </div>
        </div>
      </SectionShell>

      <SectionShell {...shell('hero', 'Homepage headline')}>
        <label className="field-label" htmlFor="hero-title">Headline</label>
        <input id="hero-title" className="input" maxLength={120} value={forms.hero.title}
          onChange={(e) => setForm('hero', { ...forms.hero, title: e.target.value })} />
        <label className="field-label" htmlFor="hero-subtitle">Subtitle</label>
        <input id="hero-subtitle" className="input" maxLength={300} value={forms.hero.subtitle}
          onChange={(e) => setForm('hero', { ...forms.hero, subtitle: e.target.value })} />
      </SectionShell>

      <SectionShell {...shell('playbook_descriptions', 'Odikai playbook descriptions', 'Shown on Picks and on each playbook page. Leave one blank to use the default.')}>
        {Object.values(PLAYBOOK_TYPES).map((type) => (
          <div key={type}>
            <label className="field-label" htmlFor={`desc-${type}`}>{titleCase(type)}</label>
            <textarea id={`desc-${type}`} className="input admin-textarea" rows={2} maxLength={1000}
              value={forms.playbook_descriptions[type] || ''}
              onChange={(e) => setForm('playbook_descriptions', { ...forms.playbook_descriptions, [type]: e.target.value })} />
          </div>
        ))}
      </SectionShell>

      <SectionShell {...shell('home_faq', 'Homepage questions', 'The "Common questions" list on the homepage.')}>
        <FaqEditor items={forms.home_faq} onChange={(v) => setForm('home_faq', v)} />
      </SectionShell>

      <SectionShell {...shell('faq', 'FAQ page', 'The questions on /faq.')}>
        <FaqEditor items={forms.faq} onChange={(v) => setForm('faq', v)} />
      </SectionShell>
    </div>
  );
}
